import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { checklistProductSearchScore } from '@tcvs/shared';

export interface FilterableSelectOption {
  value: string;
  label: string;
  searchText?: string;
}

export function filterSelectOptions(
  options: readonly FilterableSelectOption[],
  query: string,
  matchMode: 'contains' | 'checklist-fuzzy' = 'contains',
): FilterableSelectOption[] {
  if (matchMode === 'checklist-fuzzy') {
    return options
      .map((option, index) => ({
        option,
        index,
        score: checklistProductSearchScore(query, `${option.label} ${option.searchText ?? ''}`),
      }))
      .filter(
        (match): match is { option: FilterableSelectOption; index: number; score: number } =>
          match.score !== undefined,
      )
      .sort((left, right) => left.score - right.score || left.index - right.index)
      .map(({ option }) => option);
  }
  const tokens = query.trim().toLocaleLowerCase('en-US').split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [...options];
  return options.filter((option) => {
    const haystack = `${option.label} ${option.searchText ?? ''}`.toLocaleLowerCase('en-US');
    return tokens.every((token) => haystack.includes(token));
  });
}

interface FilterableSelectProps {
  value: string;
  options: readonly FilterableSelectOption[];
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  ariaLabel: string;
  onQueryChange?: (query: string) => void;
  loading?: boolean;
  searchError?: string | null;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  matchMode?: 'contains' | 'checklist-fuzzy';
  showAllOnFocus?: boolean;
}

export function FilterableSelect({
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
  onQueryChange,
  loading = false,
  searchError = null,
  hasMore = false,
  onLoadMore,
  onRetry,
  matchMode = 'contains',
  showAllOnFocus = false,
}: FilterableSelectProps): JSX.Element {
  const listboxId = useId();
  const listboxRef = useRef<HTMLDivElement>(null);
  const scrollKeyboardHighlight = useRef(false);
  const selectedLabel = options.find((option) => option.value === value)?.label ?? '';
  const [query, setQuery] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [queryEdited, setQueryEdited] = useState(false);
  const filtered = useMemo(
    () => filterSelectOptions(options, showAllOnFocus && !queryEdited ? '' : query, matchMode),
    [matchMode, options, query, queryEdited, showAllOnFocus],
  );

  useEffect(() => {
    if (!open) setQuery(selectedLabel);
  }, [open, selectedLabel]);

  useEffect(() => {
    if (!open || !scrollKeyboardHighlight.current) return;
    scrollKeyboardHighlight.current = false;
    listboxRef.current
      ?.querySelectorAll<HTMLElement>('[role="option"]')
      [highlighted]?.scrollIntoView?.({ block: 'nearest' });
  }, [highlighted, open]);

  const select = (option: FilterableSelectOption) => {
    onChange(option.value);
    setQuery(option.label);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      scrollKeyboardHighlight.current = false;
      setOpen(false);
      setQuery(selectedLabel);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      scrollKeyboardHighlight.current = true;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setHighlighted((current) => Math.max(0, Math.min(filtered.length - 1, current + direction)));
      return;
    }
    const highlightedOption = filtered[highlighted];
    if (event.key === 'Enter' && open && highlightedOption !== undefined) {
      event.preventDefault();
      select(highlightedOption);
    }
  };

  return (
    <div
      className="tcvs-filterable-select"
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setOpen(false);
        setQuery(selectedLabel);
      }}
    >
      <input
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={open}
        autoComplete="off"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={(event) => {
          scrollKeyboardHighlight.current = false;
          setOpen(true);
          setQueryEdited(false);
          setHighlighted(0);
          onQueryChange?.('');
          event.currentTarget.select();
        }}
        onChange={(event) => {
          scrollKeyboardHighlight.current = false;
          setQueryEdited(true);
          setQuery(event.target.value);
          onQueryChange?.(event.target.value);
          setOpen(true);
          setHighlighted(0);
          if (event.target.value === '') onChange('');
        }}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          className="tcvs-filterable-select-options"
        >
          {loading ? <p role="status">Searching…</p> : null}
          {searchError !== null ? <p role="alert">Search incomplete: {searchError}</p> : null}
          {!loading && searchError === null && filtered.length === 0 ? (
            <p>
              {hasMore
                ? 'No matches loaded yet. More results are available.'
                : 'No matching options'}
            </p>
          ) : null}
          {filtered.map((option, index) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={index === highlighted ? 'is-highlighted' : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(option)}
            >
              {option.label}
            </button>
          ))}
          {!loading && (searchError !== null || hasMore) ? (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={searchError !== null ? onRetry : onLoadMore}
            >
              {searchError !== null ? 'Retry search' : 'Load more checklists'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
